import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useState, useEffect } from "react";
import {
  Button,
  Flyout,
  FlyoutHeader,
  FlyoutTitle,
} from "@monolith-forensics/monolith-ui";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  TrashIcon,
  UserX,
} from "lucide-react";

import {
  DetailItem,
  DetailLabel,
  DetailValue,
  FlyoutContainer,
  HeaderContent,
} from "../../../components/Flyouts";
import ContactsColumnDefs from "../../../components/Contacts/ContactsColumnDefs";
import ContactsAPI from "@/api/contacts";
import CustomFieldsApi from "@/api/custom-fields";
import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { addCustomFieldsToColumnDefs } from "../../../components/Evidence/CustomFieldCell";
import { monolithMoment } from "../../../utils/date-format";
import { CustomAttribute } from "@/types";
import { Contact } from "../contacts.types";

import UnassignContactModal from "../../../components/Modals/UnassignContactModal";
import { EditContactModal } from "../Modals/EditContactModal";
import { DeleteContactModal } from "../Modals/DeleteContactModal";

interface ContactFlyoutProps {
  show: boolean;
  onClose?: () => void;
  defaultInfo: Contact;
  onEdit: () => void;
  onDelete: () => void;
  onNext: () => void;
  onPrevious: () => void;
  caseId?: number | undefined;
}

interface MainDetailsProps {
  defaultData: Contact;
}

const MainDetails: FC<MainDetailsProps> = ({ defaultData }) => {
  const [columnState, setColumnState] = useState(ContactsColumnDefs);
  const dataFieldIgnoreList = ["notes"];

  // Fetch custom fields for contacts
  const { data: customFields } = useQuery({
    queryKey: ["contacts", "customFields", "list"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.PEOPLE,
      }),
  });

  // Update column definitions with custom fields
  useEffect(() => {
    if (!customFields) return;
    setColumnState((cs) => {
      return addCustomFieldsToColumnDefs(customFields, cs);
    });
  }, [customFields]);

  // Transform data to include custom field values
  const data: Contact & Record<string, any> = {
    ...defaultData,
    ...(defaultData?.custom_attributes?.reduce(
      (
        acc: Record<string, string | number | string[]>,
        attr: CustomAttribute
      ) => {
        acc[`custom_field_${attr.field_id}`] = attr.value;
        return acc;
      },
      {} as Record<string, string | number | string[]>
    ) || {}),
  };
  return (
    <FlyoutContainer>
      <div className="event-details">
        {columnState
          .filter((col) => !dataFieldIgnoreList.includes(col.dataField))
          .filter((col) =>
            col.dataField.includes("custom_field_")
              ? !!data[col.dataField]
              : true
          )
          .sort((a, b) => {
            // Sort custom fields alphabetically by caption, keep regular fields in original order
            const aIsCustom = a.dataField.includes("custom_field_");
            const bIsCustom = b.dataField.includes("custom_field_");

            if (aIsCustom && bIsCustom) {
              return a.caption.localeCompare(b.caption);
            }

            // Keep original order for non-custom fields
            return 0;
          })
          .map((col) => {
            const key = col.dataField;
            const label = col.caption;
            let value: React.ReactNode;

            if (col?.render) {
              value = col.render(data);
            } else if ((col as any).dataType === "date") {
              value = monolithMoment({
                timestamp: data[key],
                includeTime: true,
              });
            } else {
              value = data[key];
            }

            return (
              <DetailItem key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailItem>
            );
          })}
        <DetailItem>
          <DetailLabel>Notes</DetailLabel>
        </DetailItem>
        <DetailValue>
          {defaultData.notes || "No notes for this contact"}
        </DetailValue>
      </div>
    </FlyoutContainer>
  );
};

export const ContactFlyout: React.FC<ContactFlyoutProps> = ({
  show,
  onClose,
  defaultInfo,
  onEdit,
  onDelete,
  onNext,
  onPrevious,
  caseId,
}) => {
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showUnassignModal, setShowUnassignModal] = useState<boolean>(false);
  const { data: itemData, isLoading } = useQuery({
    queryKey: [
      "contact",
      "item",
      defaultInfo.client_id,
      { include_custom_attributes: true },
    ],
    queryFn: () =>
      ContactsAPI.getContacts({
        client_id: defaultInfo.client_id,
        include_custom_attributes: true,
      }),
    initialData: [defaultInfo],
    select: (res) => {
      return res[0];
    },
  });

  return (
    <Flyout open={show} onClose={() => onClose?.()}>
      {isLoading && <div>Loading...</div>}
      {!isLoading && (
        <>
          <FlyoutHeader>
            <FlyoutTitle>{itemData.name}</FlyoutTitle>
            <EditContactModal
              onSubmit={() => {
                queryClient.invalidateQueries({
                  queryKey: [
                    "contact",
                    "item",
                    defaultInfo.client_id,
                    { include_custom_attributes: true },
                  ],
                });
                queryClient.invalidateQueries({
                  queryKey: ["contacts", "list"],
                });
                onEdit();
              }}
              show={showEditModal}
              onClose={() => setShowEditModal(false)}
              onCancel={() => setShowEditModal(false)}
              defaultInfo={itemData}
            />
            <DeleteContactModal
              show={showDeleteModal}
              onCancel={() => setShowDeleteModal(false)}
              onClose={() => setShowDeleteModal(false)}
              onSubmit={() => {
                onDelete?.(); // Refetches queries in contact section
                setShowDeleteModal(false);
                // Close the flyout when in case context
                if (caseId) {
                  onClose?.();
                }
              }}
              defaultInfo={itemData}
              caseId={caseId}
            />
            <HeaderContent>
              <div>Contact</div>
              <Button
                size="xxs"
                variant="text"
                leftSection={<EditIcon size={14} />}
                color="primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
              <Button
                size="xxs"
                variant="text"
                leftSection={<TrashIcon size={14} />}
                color="error"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
              {!!caseId && (
                <Button
                  size="xxs"
                  variant="text"
                  color="orange"
                  leftSection={<UserX size={14} />}
                  onClick={() => setShowUnassignModal(true)}
                >
                  Unassign Contact
                </Button>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center",
                  marginLeft: "auto",
                }}
              >
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onPrevious?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronLeftIcon size={14} />
                </Button>
                <Button
                  size="xxs"
                  variant="outlined"
                  onClick={() => onNext?.()}
                  style={{ padding: "2px 4px" }}
                >
                  <ChevronRightIcon size={14} />
                </Button>
              </div>
            </HeaderContent>
          </FlyoutHeader>
          <MainDetails defaultData={itemData} />
        </>
      )}
      <EditContactModal
        onSubmit={() => {
          queryClient.invalidateQueries({
            queryKey: [
              "contact",
              "item",
              defaultInfo.client_id,
              { include_custom_attributes: true },
            ],
          });
          queryClient.invalidateQueries({
            queryKey: ["contacts", "list"],
          });
          onEdit();
        }}
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        onCancel={() => setShowEditModal(false)}
        defaultInfo={itemData}
      />
      <DeleteContactModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        onSubmit={() => {
          onDelete?.(); // Refetches queries in contact section
          setShowDeleteModal(false);
          // Close the flyout when in case context
          if (caseId) {
            onClose?.();
          }
        }}
        defaultInfo={itemData}
        caseId={caseId}
      />
      <UnassignContactModal
        show={showUnassignModal}
        onCancel={() => setShowUnassignModal(false)}
        onClose={() => setShowUnassignModal(false)}
        onSubmit={() => {
          onDelete?.();
          setShowUnassignModal(false);
        }}
        defaultInfo={itemData}
        caseId={caseId}
      />
    </Flyout>
  );
};
